import { ParagraphText } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-auto-save/interface/auto-save-interface';
import { ITextBodyStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import {
    IParagraph,
    ITextRun,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { IPoint } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import has from 'lodash/has';
import { DocumentElement, ParagraphElement, TextElement, WhiteSpaceType } from '@tencent/alloy-editor';
import ParagraphMeasure from '@src/slide/domain/ui-model/element-modeles/property/text/editor-measure/para-measure';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { isEditorEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { isLatinLang } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/language';

/**
 * textStyle 根据中英文挂上lang
 * @param text
 * @param textRunStyle
 */
export function setLangFromText(text, textRunStyle) {
    if (!isLatinLang(text)) {
        textRunStyle.lang = 'ea';
        // 有一种情况自带的lang为ea，但文本不是中文，根据office表现以isLatinLang为主
    } else if (textRunStyle.lang === 'ea' && isLatinLang(text)) {
        textRunStyle.lang = 'latin';
    } else {
        // 其他西文字体
        textRunStyle.lang = textRunStyle.lang ?? 'latin';
    }
}

export function getParaTextWithEditorValue(value) {
    const paraList: any[] = value?.children;
    const paraText: ParagraphText[] = [];
    paraList.forEach((para, index) => {
        const text = (para.children || []).reduce((memo, c) => memo + c.text, '');
        paraText.push({ paraIndex: index, text });
    });
    return paraText;
}

// 加工用于设置样式的style
export function processSavedStyles(styles) {
    if (!styles || typeof styles !== 'object') {
        return undefined;
    }
    const allStyles = {};
    Object.keys(styles).forEach((i) => {
        allStyles[i] = styles[i];
        allStyles[`origin-${i}`] = styles[i];
        allStyles[`origin-raw-${i}`] = true;
    });
    return allStyles;
}


/**
 * 合并两个TextBodyStyle
 * @param source
 * @param target
 */
export function mergeTextBodyStyle(source: ITextBodyStyle, target: ITextBodyStyle): ITextBodyStyle {
    return {
        wrapperStyle: { ...source.wrapperStyle, ...target.wrapperStyle },
        bodyStyle: { ...source.bodyStyle, ...target.bodyStyle },
        autoFitType: has(target, '.autoFitType') ? target.autoFitType : source.autoFitType,
    };
}

/**
 * 将Point中 基于textRun的offset转成基于paragraph的Offset
 * @param point
 * @param paragraphList
 */
export function convertPointToParagraphOffset(point: IPoint, paragraphList: ParagraphElement[]): IPoint {
    const [paragraphIndex, textRunIndex] = point.path;

    const preTextRuns = paragraphList[paragraphIndex]?.children?.slice(0, textRunIndex);

    if (preTextRuns) {
        const preOffset = preTextRuns
            .reduce((memo, currentValue) => memo + (currentValue.text?.length || 0), 0);

        return {
            path: point.path,
            offset: preOffset + point.offset,
        };
    }

    return point;
}

export function convertPointToRunOffset(point: IPoint, paragraphList: IParagraph[]): IPoint {
    const [paragraphIndex] = point.path;
    const paragraphOffset = point.offset;
    const paragraph = paragraphList[paragraphIndex];
    if (!paragraph) return point;
    let currentIndex = 0;
    const textRunsLength = paragraph.textRuns?.length || 0;
    for (let i = 0; i < textRunsLength; i++) {
        const run = paragraph.textRuns?.[i];
        const textLength = run?.text?.length || 0;
        if (currentIndex + textLength >= paragraphOffset) {
            return {
                path: [paragraphIndex, i],
                offset: paragraphOffset - currentIndex,
            };
        }
        currentIndex += textLength;
    }
    return point;
}

/**
 * 文本探测逻辑。减小padding来达到使内容尽量显示成一行的目的
 * @param documentElement
 * @param key
 * @param textWidth
 */
export function updateEditorPadding(documentElement: DocumentElement, key: string, textWidth: number) {
    const docElement = documentElement;
    const { paddingLeft = 0, paddingRight = 0, paddingChanged = false } = docElement;
    if (!paddingChanged && checkPaddingOffsetOrNot(key, docElement, paddingLeft, paddingRight)) {
        // 如果不存在去计算，否则取缓存
        const paddingOffset = ParagraphMeasure.
            computePaddingOffset(key, docElement, textWidth, paddingLeft, paddingRight);
        if (paddingOffset && (paddingOffset.offsetPaddingLeft > 0 || paddingOffset.offsetPaddingRight > 0)) {
            const pLeft = paddingLeft - paddingOffset.offsetPaddingLeft;
            const pRight = paddingRight - paddingOffset.offsetPaddingRight;
            docElement.paddingLeft = pLeft;
            docElement.paddingRight = pRight;
            docElement.paddingChanged = true; // 文本探测标记，防止padding重复被挤掉
        }
    }
}

/**
 * 判断是否要进行文本探测，满足以下条件
 * 存在key，document内容不为空，padding不为0，没有设置不换行，非竖排，文字未分栏
 * @param modelKey
 * @param docElement
 * @param paddingLeft
 * @param paddingRight
 * @returns
 */
function checkPaddingOffsetOrNot(modelKey: string, docElement: DocumentElement, paddingLeft, paddingRight) {
    const isVert = !!docElement.vertical;
    // 是否未分栏
    const isNotColumnized = !(docElement.columnNumber && docElement.columnNumber > 1);
    // 分栏大于一栏时会忽略不换行属性
    const nowrap = isNotColumnized && (docElement.whiteSpaceType === WhiteSpaceType.NowrapExceptForceBreak
        || docElement.whiteSpaceType === WhiteSpaceType.Nowrap);
    return modelKey && isNotColumnized && (paddingLeft > 0 || paddingRight > 0)
        && !isDocumentEmpty(docElement) && !nowrap && !isVert;
}

/**
 * 判断document element中是否有内容
 * @param docElement
 */
export function isDocumentEmpty(docElement: DocumentElement): boolean {
    let isEmpty = true;
    const paraList: ParagraphElement[] = docElement.children;
    if (paraList) {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < paraList.length; i++) {
            const paraEmpty = isParaElementEmpty(paraList[i]);
            if (!paraEmpty) {
                isEmpty = false;
                break;
            }
        }
    }
    return isEmpty;
}

/**
 * 判断是否为空文本框
 * @param model
 * @param paraList 可选参数
 * @returns boolean
 */
export function isEmptyText(model, paraList?: IParagraph[]): boolean {
    const { fillBackground, border } = model?.props?.style || {};
    const emptyPGL = isEditorEmpty(paraList || model.paragraphList);
    const hasBorder = (border?.color && border.color !== 'none') || border?.gradFill;
    const hasBackground = fillBackground && fillBackground?.noFill !== 1;
    const isNotGroupChild = [UIModelType.SLIDE, UIModelType.MASTER, UIModelType.LAYOUT].includes(model.parent?.type);
    return emptyPGL && !hasBorder && !hasBackground && isNotGroupChild;
}

export function isParaElementEmpty(paraEle: ParagraphElement): boolean {
    const textRuns: TextElement[] = paraEle.children;
    let textLength = 0;
    if (Array.isArray(textRuns)) {
        // eslint-disable-next-line no-restricted-syntax
        for (const run of textRuns) {
            textLength += (run.text ? run.text.length : 0);
            if (textLength > 0) return false;
        }
    }
    return textLength === 0;
}

export function countWords(paragraphList: IParagraph[]): number {
    let num = 0;
    paragraphList?.forEach((paragraph) => {
        paragraph.textRuns?.forEach((textRun) => {
            num += textRun?.text?.length || 0;
        });
    });
    return num;
}

export function setLangForParagraphList(pList: IParagraph[]) {
    Array.isArray(pList) && pList.forEach((p: IParagraph) => {
        const { textRuns } = p;
        Array.isArray(textRuns) && textRuns.forEach((run: ITextRun) => {
            if (!run.styles?.lang) {
                run.styles = run.styles || {};
                setLangFromText(run.text, run.styles);
            }
        });
    });
}

/**
 * 判断editor 段落是否有项目符号
 * @param paragraph
 * @returns
 */
export function hasCharacterOfParaElement(paragraph: ParagraphElement): boolean {
    const characterProps = paragraph['origin-character'];
    if (!characterProps) return false;
    if (characterProps.character && !characterProps.buNone) return true;
    return false;
}

/**
 * 判断光标是否在段落开头
 * @param anchor
 * @param focus
 * @returns boolean
 */
export function isCaretAtBeginningOfPara(anchor: IPoint, focus: IPoint): boolean {
    if (anchor.path[0] !== focus.path[0] || anchor.offset !== 0 || focus.offset !== 0) return false;
    return true;
}

export function getParaTextWithParagraphList(paragraphList) {
    const paraText: Array<{
        paraIndex: number;
        text: string;
    }> = [];
    paragraphList.forEach((para, index) => {
        const text = (para.textRuns || []).reduce((memo, c) => memo + c.text, '');
        paraText.push({ paraIndex: index, text });
    });
    return paraText;
}
